import clsx from 'clsx';
import React from 'react';
import Icon from '../../../atoms/Icon/Icon';
import Dialog from '../Dialog/Dialog';
import useDialogUploadFile from './useDialogUploadFile';

const DEFAULT_EXTENSIONS: string[] = [
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'svg',
  'mp4',
  'webm',
  'mov',
  'ogv',
  'mp3',
  'wav',
  'ogg',
  'aac',
];
const DEFAULT_MAX_SIZE_BYTES = 100 * 1024 * 1024;

export type DialogUploadFileProps = Readonly<{
  acceptedExtensions?: string[];
  ariaLabel?: string;
  buttonTextSuccess?: string;
  className?: string;
  disclaimer?: React.ReactNode;
  isMultiple?: boolean;
  isOpen?: boolean;
  isUploading?: boolean;
  maxSizeBytes?: number;
  onClose: () => void;
  onSuccess?: (files: File[]) => void;
  title?: string;
  uploadProgress?: number;
}>;

function DialogUploadFile({
  acceptedExtensions = DEFAULT_EXTENSIONS,
  ariaLabel,
  buttonTextSuccess,
  className,
  disclaimer,
  isMultiple = true,
  isOpen = false,
  isUploading = false,
  maxSizeBytes = DEFAULT_MAX_SIZE_BYTES,
  onClose,
  onSuccess,
  title,
  uploadProgress = 0,
}: DialogUploadFileProps): React.JSX.Element {
  const {
    acceptAttribute,
    allowedFormatsLabel,
    allowedFormatsText,
    browseText,
    buttonTextSuccessText,
    dragAnnouncementText,
    dropTitleText,
    errorText,
    fileInputRef,
    formatFileSize,
    getDeleteFileLabel,
    getFileIconType,
    handleClickSelect,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleFileInputChange,
    handleSuccess,
    hasFiles,
    isDragOver,
    makeHandleRemoveFile,
    maxFileSizeLabel,
    maxFileSizeText,
    rejectedFiles,
    selectedFiles,
    selectedFilesLabel,
    titleText,
    uploadFileAriaLabel,
    uploadingAriaLabel,
  } = useDialogUploadFile({
    acceptedExtensions,
    buttonTextSuccess,
    isMultiple,
    isOpen,
    maxSizeBytes,
    onSuccess,
    title,
  });

  return (
    <Dialog
      ariaLabel={ariaLabel}
      buttonTextSuccess={buttonTextSuccessText}
      className={clsx('dialog-upload-file', className)}
      isDisabledSuccess={!hasFiles || isUploading}
      isOpen={isOpen}
      onCancel={onClose}
      onClose={onClose}
      onSuccess={handleSuccess}
      size="xl"
      title={titleText}
    >
      <input
        ref={fileInputRef}
        accept={acceptAttribute}
        aria-hidden="true"
        aria-label={uploadFileAriaLabel}
        className="dialog-upload-file__input"
        multiple={isMultiple}
        tabIndex={-1}
        type="file"
        onChange={handleFileInputChange}
      />
      <section
        className={clsx('upload-file-container__drag-container', {
          'upload-file-container__drag-container--drag-over': isDragOver,
          'upload-file-container__drag-container--has-files': hasFiles,
        })}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <span aria-live="polite" className="sr-only">
          {isDragOver ? dragAnnouncementText : ''}
        </span>
        <div className="upload-file-container__drag-info">
          <span aria-hidden="true" className="upload-file-container__drag-icon">
            <Icon color="primary" name="upload" size="xl" />
          </span>

          <div className="upload-file-container__text-container">
            <span className="upload-file-container__drag-title">{dropTitleText}</span>
            <button className="upload-file-container__link" type="button" onClick={handleClickSelect}>
              {browseText}
            </button>
          </div>

          <div className="upload-file-container__max-size">
            {maxFileSizeText}: {maxFileSizeLabel}
          </div>
          <div className="upload-file-container__max-size">
            {allowedFormatsText}: {allowedFormatsLabel}
          </div>
        </div>
      </section>

      {disclaimer && <div className="dialog-upload-file__disclaimer">{disclaimer}</div>}

      {rejectedFiles.length > 0 && (
        <p className="upload-file-container__error" role="alert">
          {errorText}: {rejectedFiles.join(', ')}
        </p>
      )}
      {hasFiles && (
        <ul aria-label={selectedFilesLabel} aria-live="polite" className="upload-file-list">
          {selectedFiles.map((file, index) => {
            const handleRemoveFile = makeHandleRemoveFile(index);
            return (
              <li key={`${file.name}-${index}`} className="upload-file-item">
                <div className="upload-file-item__icon-container">
                  <Icon color="primary" name={getFileIconType(file.name)} size="md" />
                </div>
                <div className="upload-file-item__info">
                  <span className="upload-file-item__name">{file.name}</span>
                  {isUploading ? (
                    <div
                      aria-label={uploadingAriaLabel}
                      aria-valuemax={100}
                      aria-valuemin={0}
                      aria-valuenow={uploadProgress}
                      className="upload-file-item__progress"
                      role="progressbar"
                    >
                      <span className="upload-file-item__progress-bar" style={{ width: `${uploadProgress}%` }} />
                    </div>
                  ) : (
                    <span className="upload-file-item__size">{formatFileSize(file.size)}</span>
                  )}
                </div>
                {!isUploading && (
                  <button
                    aria-label={getDeleteFileLabel(file.name)}
                    className="upload-file-item__delete"
                    type="button"
                    onClick={handleRemoveFile}
                  >
                    <Icon color="grey-02" name="trash" size="md" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Dialog>
  );
}

export default DialogUploadFile;
